import { Select, Upload, UploadFile, UploadProps } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getUser,
  getUserRoles,
  saveUser,
  updateUser,
} from "../../apis/user";
import { Role } from "../../interfaces/User";
import { useNotification } from "../../NotificationContext";
import { LanguageContext } from "../../providers/LanguageProvider";
import { clinicVisitType } from "../../interfaces/Visit";
import { getClinicVisitTypes } from "../../apis/visit";
import { UploadChangeParam, RcFile } from "antd/es/upload";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { getDepartments } from "../../apis/departments";

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "hi", label: "Hindi" },
  { value: "mr", label: "Marathi" },
];

const SectionHeader: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <div className="border-b border-gray-200 pb-2 mb-4">
    <h4 className="text-base font-semibold text-gray-800">{title}</h4>
    {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
  </div>
);

const Field: React.FC<{ label: React.ReactNode; required?: boolean; error?: string; children: React.ReactNode }> = ({
  label, required, error, children,
}) => (
  <div className="block">
    <span className="text-sm font-medium text-gray-700">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </span>
    <div className="mt-1">{children}</div>
    {error && <span className="text-sm text-red-500">{error}</span>}
  </div>
);

const inputCls = "block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm";

const AddUser: React.FC<{}> = () => {
  const [user, setUser] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    phone: "",
    email: "",
    degree: "",
    registrationNumber: "",
    address: "",
    password: "",
    preferredLanguage: "en",
    roles: [] as any[],
    defaultClinicVisitTypeId: "",
    departmentId: "",
    signature: null as any,
    payoutMode: "" as "" | "percentage" | "fixed",
    payoutValue: "" as number | "",
    payoutCap: "" as number | "",
  });
  const [roles, setRoles] = useState<Array<Role>>([]);
  const [clinicVisitTypes, setClinicVisitTypes] = useState<Array<clinicVisitType>>([]);
  const [errors, setErrors] = useState<any>();
  const [loggedInUser, setLoggedInUser] = useState<any>();
  const { showNotification } = useNotification();
  const { userId } = useParams();
  const navigate = useNavigate();
  const { translate: t } = useContext(LanguageContext);
  const [loading, setLoading] = useState(false);
  const [, setFile] = useState<any>();
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [departments, setDepartments] = useState<any[]>([]);

  const isDoctor = user.roles.some((r: any) =>
    String(r).toLowerCase().includes("doctor")
  );

  useEffect(() => {
    getDepartments().then((res: any) => setDepartments(res.data ?? []));
  }, []);

  useEffect(() => {
    const userObj = window.localStorage.getItem("user");
    if (userObj) setLoggedInUser(JSON.parse(userObj));
  }, []);

  useEffect(() => {
    if (userId) fetchUser(userId);
    fetchRoles();
    fetchClinicVisitTypes();
  }, [userId]);

  const fetchRoles = () => {
    getUserRoles().then((res: any) => setRoles(res.data));
  };

  const fetchClinicVisitTypes = () => {
    getClinicVisitTypes("opd").then((res: any) =>
      setClinicVisitTypes(res.data ?? res ?? [])
    );
  };

  const fetchUser = (id: any) => {
    getUser(id).then((res: any) => {
      const data = res.data;
      setUser({
        firstName: data.firstName ?? "",
        middleName: data.middleName ?? "",
        lastName: data.lastName ?? "",
        phone: data.phone ?? "",
        email: data.email ?? "",
        degree: data.degree ?? "",
        registrationNumber: data.registrationNumber ?? "",
        address: data.address ?? "",
        password: "",
        preferredLanguage: data.preferredLanguage ?? "en",
        roles: data.roles ?? [],
        defaultClinicVisitTypeId: data.defaultClinicVisitTypeId ?? "",
        departmentId: data.department?.id ?? "",
        signature: data.signature ?? null,
        payoutMode: data.payoutMode ?? "",
        payoutValue: data.payoutValue ?? "",
        payoutCap: data.payoutCap ?? "",
      });
    });
  };

  const set = (field: string, value: any) => setUser((u) => ({ ...u, [field]: value }));

  const onSave = () => (userId ? updateUserData(userId) : saveUserData());

  const saveUserData = async () => {
    const { hasErrors, validationErrors } = validateForm();
    if (hasErrors) { setErrors(validationErrors); return; }
    setErrors(null);
    saveUser({ ...user })
      .then(() => {
        showNotification("success", "User added successfully", "", "topRight");
        navigate("/users");
      })
      .catch((err) => {
        if (err.response?.data?.errors) setErrors(err.response.data.errors);
        showNotification("error", err.response.data.message, "", "topRight");
      });
  };

  const updateUserData = async (id: any) => {
    const { hasErrors, validationErrors } = validateForm();
    if (hasErrors) { setErrors(validationErrors); return; }
    setErrors(null);
    updateUser({ ...user, id })
      .then(() => {
        if (parseInt(id) === loggedInUser?.id)
          window.localStorage.setItem("user", JSON.stringify(user));
        showNotification("success", "User updated successfully", "", "topRight");
        navigate("/users");
      })
      .catch((err) => {
        if (err.response?.data?.errors) setErrors(err.response.data.errors);
        showNotification("error", err.response.data.message, "", "topRight");
      });
  };

  const validateForm = () => {
    const validationErrors: any = {};
    if (!user.firstName) validationErrors.firstName = "* First name is required";
    if (!user.lastName) validationErrors.lastName = "* Last name is required";
    if (!user.phone) {
      validationErrors.phone = "* Phone is required";
    } else if (user.phone.length !== 10) {
      validationErrors.phone = "Phone number should be 10 digits";
    }
    if (!user.email) {
      validationErrors.email = "* Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email)) {
      validationErrors.email = "Invalid email format";
    }
    if (!userId && !user.password) validationErrors.password = "* Password is required";
    if (user.roles.length === 0) validationErrors.roles = "Select at least one role";
    return { hasErrors: Boolean(Object.keys(validationErrors).length), validationErrors };
  };

  const beforeUploadingFile = (file: RcFile) => {
    setFileError(null);
    const isImage = file.type === "image/png" || file.type === "image/jpeg";
    if (!isImage) { setFileError("Only JPG or PNG files are allowed."); return Upload.LIST_IGNORE; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.src = url;
    img.onload = () => {
      setFileUrl(file.name);
      setUser((u) => ({ ...u, signature: file as any }));
      URL.revokeObjectURL(url);
    };
    return isImage;
  };

  const handleFileChange: UploadProps["onChange"] = (info: UploadChangeParam<UploadFile>) => {
    setLoading(true);
    setFile(info.file);
    setLoading(false);
  };

  return (
    <div className="flex flex-col flex-grow p-6 bg-white rounded-lg">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">
        {userId ? t("update") : t("add")} {t("user")}
      </h3>

      {/* ── Section 1: Basic Information ── */}
      <div className="mb-8">
        <SectionHeader title="Basic Information" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Field label={t("first_name")} required error={errors?.firstName}>
            <input
              type="text"
              className={inputCls}
              value={user.firstName}
              onChange={(e) => /^[a-zA-Z]*$/.test(e.target.value) && set("firstName", e.target.value)}
            />
          </Field>
          <Field label={t("middle_name")}>
            <input
              type="text"
              className={inputCls}
              value={user.middleName}
              onChange={(e) => /^[a-zA-Z]*$/.test(e.target.value) && set("middleName", e.target.value)}
            />
          </Field>
          <Field label={t("last_name")} required error={errors?.lastName}>
            <input
              type="text"
              className={inputCls}
              value={user.lastName}
              onChange={(e) => /^[a-zA-Z]*$/.test(e.target.value) && set("lastName", e.target.value)}
            />
          </Field>
          <Field label={t("phone")} required error={errors?.phone}>
            <input
              type="text"
              className={inputCls}
              value={user.phone}
              maxLength={10}
              onChange={(e) => /^\+?[0-9]*$/.test(e.target.value) && set("phone", e.target.value)}
            />
          </Field>
          <Field label={t("email")} required error={errors?.email}>
            <input
              type="text"
              className={inputCls}
              value={user.email}
              onChange={(e) => set("email", e.target.value)}
            />
          </Field>
          <Field label={t("address")}>
            <input
              type="text"
              className={inputCls}
              value={user.address}
              onChange={(e) => set("address", e.target.value)}
            />
          </Field>
          {!userId && (
            <Field label="Password" required error={errors?.password}>
              <input
                type="password"
                className={inputCls}
                value={user.password}
                onChange={(e) => set("password", e.target.value)}
                autoComplete="new-password"
              />
            </Field>
          )}
        </div>
      </div>

      {/* ── Section 2: Role & Access ── */}
      <div className="mb-8">
        <SectionHeader title="Role & Access" subtitle="Assign roles and system access settings" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Field label={t("role")} required error={errors?.roles}>
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              onChange={(v) => set("roles", v)}
              value={user.roles}
            >
              {roles.map((role: any) => (
                <Select.Option key={role.id} value={role.name}>
                  {role.name}
                </Select.Option>
              ))}
            </Select>
          </Field>
          <Field label="Department">
            <Select
              style={{ width: "100%" }}
              allowClear
              onChange={(v) => set("departmentId", v ?? "")}
              value={user.departmentId || undefined}
              placeholder="Select department"
            >
              {departments.map((dept: any) => (
                <Select.Option key={dept.id} value={dept.id}>
                  {dept.name}
                </Select.Option>
              ))}
            </Select>
          </Field>
          <Field label="Preferred Language">
            <Select
              style={{ width: "100%" }}
              onChange={(v) => set("preferredLanguage", v)}
              value={user.preferredLanguage}
            >
              {LANGUAGES.map((l) => (
                <Select.Option key={l.value} value={l.value}>
                  {l.label}
                </Select.Option>
              ))}
            </Select>
          </Field>
        </div>
      </div>

      {/* ── Section 3: Clinical Profile (doctors only) ── */}
      {isDoctor && (
        <div className="mb-8">
          <SectionHeader
            title="Clinical Profile"
            subtitle="Doctor-specific settings — qualification, default visit type, and signature"
          />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Field label={t("degree")}>
              <input
                type="text"
                className={inputCls}
                value={user.degree}
                onChange={(e) => set("degree", e.target.value)}
              />
            </Field>
            <Field label={t("registration_number")}>
              <input
                type="text"
                className={inputCls}
                value={user.registrationNumber}
                onChange={(e) => set("registrationNumber", e.target.value)}
              />
            </Field>
            <Field label="Default Visit Type">
              <Select
                style={{ width: "100%" }}
                allowClear
                onChange={(v) => set("defaultClinicVisitTypeId", v ?? "")}
                value={user.defaultClinicVisitTypeId || undefined}
                placeholder="Select visit type"
              >
                {clinicVisitTypes.map((cvt: any) => (
                  <Select.Option key={cvt.id} value={cvt.id}>
                    {cvt.name}
                  </Select.Option>
                ))}
              </Select>
            </Field>
            <Field label="Payout Mode">
              <Select
                style={{ width: "100%" }}
                allowClear
                onChange={(v) => set("payoutMode", v ?? "")}
                value={user.payoutMode || undefined}
                placeholder="Same as service (no override)"
              >
                <Select.Option value="percentage">Percentage of fee</Select.Option>
                <Select.Option value="fixed">Fixed amount</Select.Option>
              </Select>
            </Field>
            {user.payoutMode && (
              <>
                <Field label={user.payoutMode === "percentage" ? "Payout %" : "Payout Amount (₹)"}>
                  <input
                    type="number"
                    min={0}
                    step={user.payoutMode === "percentage" ? 1 : 0.01}
                    max={user.payoutMode === "percentage" ? 100 : undefined}
                    className={inputCls}
                    value={user.payoutValue}
                    onChange={(e) => set("payoutValue", e.target.value === "" ? "" : parseFloat(e.target.value))}
                    placeholder={user.payoutMode === "percentage" ? "e.g. 30" : "e.g. 500"}
                  />
                </Field>
                <Field label="Payout Cap (₹)" >
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    className={inputCls}
                    value={user.payoutCap}
                    onChange={(e) => set("payoutCap", e.target.value === "" ? "" : parseFloat(e.target.value))}
                    placeholder="Max payout per service (optional)"
                  />
                </Field>
              </>
            )}
            <div className="lg:col-span-3">
              <Field label="Signature">
                <div className="flex items-start gap-4">
                  <Upload
                    name="file"
                    listType="picture-card"
                    showUploadList={false}
                    beforeUpload={beforeUploadingFile}
                    onChange={handleFileChange}
                    customRequest={(arg) => setFile(arg.file)}
                  >
                    {fileUrl ? (
                      <div className="text-xs px-2">{fileUrl}</div>
                    ) : (
                      <div className="p-4 cursor-pointer">
                        {loading ? <LoadingOutlined /> : <PlusOutlined />}
                        <div style={{ marginTop: 8 }}>Upload</div>
                      </div>
                    )}
                  </Upload>
                  {!fileUrl && user.signature && (
                    <img
                      src={user.signature}
                      alt="Signature"
                      className="max-h-24 object-contain border border-gray-200 rounded p-1"
                    />
                  )}
                </div>
                {fileError && <p className="mt-1 text-sm text-red-600">{fileError}</p>}
                <p className="mt-1 text-xs text-gray-400">JPG or PNG</p>
              </Field>
            </div>
          </div>
        </div>
      )}

      <div className="flex mt-2">
        <button
          onClick={onSave}
          type="submit"
          className="inline-flex justify-center px-6 py-2 text-sm font-medium rounded-md border border-transparent shadow-sm text-light bg-primary hover:bg-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          {userId ? t("update") : t("submit")}
        </button>
        <button
          type="button"
          onClick={() => navigate("/users")}
          className="ml-3 inline-flex justify-center px-6 py-2 text-sm font-medium rounded-md border border-gray-300 shadow-sm text-gray-700 bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AddUser;
